'use client';

import { Button } from '@nf/ui';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useState, useEffect, useRef } from 'react';
import { SearchIcon, DropdownIcon, UserIcon, QuestionIcon } from '@/components/icons/svg-icons';
import { Modal } from '@/components/widgets/modal';
import { SearchComponent } from '@/features/search/search-component';
import { useAccountStatus } from '@/hooks/query/account';
import { headerStyle } from '../header.style';
import { BalanceInfo } from '../balance-info';
import { useSiteStore } from '@/store/site';
import { searchService } from '@/services/search.service';
import { wcPromotionMissionService } from '@/domains/wc-promotion/services/wc-promotion-mission.service';
import { useObservableState, useOuterClick } from '@nf/utils-common/hooks';
import { ProfileSimpleInfo } from '@/features/profile/profile-section/components/profile-simple-info';
import { useAccountBalance, useCurrentProperties, useMenuTypeList, useSwitchParlayRoute } from '@/hooks/query';
import { useRouter } from '@/navigation';
import { useParams, useSelectedLayoutSegments } from 'next/navigation';
import { profilePanelService } from '@/services/profile-panel.service';
import { ViewEnum } from '@/types';
import { TaskTypeEnum } from '@/domains/wc-promotion/types';
import { useAppearanceStore } from '@/store/appearance';
import { tooltip } from 'styled-system/recipes';
import { TutorialTooltip } from '@/features/tutorial/tutorial-tooltip';
import { modalService } from '@/services/modal.service';
import { isNumberGames } from '@/services/settings.service';
import { sportService } from '@/services/sport.service';
import { useAuthToken } from '@/store/auth';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { Tooltip } from '@/components/widgets/tooltip';
import webStorageKeys from '@/constants/web-storage-keys';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { TutorialParlay } from '@/features/tutorial/tutorial-parlay';
import { useAnalyseAttribute } from '@/features/next-analyse';
import { AccountTooltip } from '@/features/profile/account-tooltip';
import { siteService } from '@/services/site.service';
import { LicenseeTypeEnums } from '@nf/types/site';
import { headerStyle as style } from '@/jupiter/components/header/header.style';
import { BalancePanel } from '@/jupiter/components/header/components/balance-panel';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { Breakpoint } from '@/features/breakpoint';
import { isOddsPageLoadingAtom } from '@/atoms/odds-page';
import { useAtomValue } from 'jotai';

import { AccountActStatus } from '@nf/utils-common/constants';
import { accountService } from '@/services/account.service';
import { useAccountInfo } from '@/hooks/query/account';
import { useQueryClient } from '@tanstack/react-query';
import { PopUpFeatureId } from '@nf/types/account';
import { is1x2CustomizationSettingSport } from '@/services/settings.service';
import { useOddsPageSwitcher } from '@/hooks/use-odds-page-switcher';

const SearchButton = ({ clickHandler }: { clickHandler: () => void }) => {
	const taggedElement = useAnalyseAttribute('switch-to-search-via-header');
	return (
		<Button
			variant="icon"
			className={headerStyle.searchButton}
			onClick={clickHandler}
			ref={taggedElement}
		>
			<SearchIcon />
		</Button>
	);
};
export const HeaderTools = () => {
	const [
		isParlayMode,
		setCurrentPage,
		currentPage,
	] = useSiteStore(state => [
		state.isParlayMode,
		state.setCurrentPage,
		state.currentPage,
	]);
	const { switchToParlay, disableParlay } = useOddsPageSwitcher();
	const { sport } = useCurrentProperties();
	const selectedLayoutSegments = useSelectedLayoutSegments();
	const showSearchModal = useObservableState(searchService.onSearchModal$, false);
	const [showBalanceInfo, setShowBalanceInfo] = useState(false);
	const [isShowBalance, setIsShowBalance] = useSiteStore(state => [state.isShowBalance, state.setIsShowBalance]);
	const t = useTranslations('General');
	const authToken = useAuthToken();
	const { data: actStatus } = useAccountStatus();
	const { menuTypeList } = useMenuTypeList();
	const { refetch: refetchBalanceInfo } = useAccountBalance();
	const { sport: targetSport, market: targetMarket } = useSwitchParlayRoute(!isParlayMode);
	const locale = useLocale();
	const router = useRouter();
	const isBasketball2K = Number(sport) === 134;
	const isVirtualSportsProduct = sportService.isAnyVirtualSportsProduct(sport);
	const isSportsTab = selectedLayoutSegments.includes('sports');
	const showParlayButton = (
		(
			isSportsTab ||
			selectedLayoutSegments.includes('world') ||
			selectedLayoutSegments.includes('favorites')
		) && (
			!isVirtualSportsProduct
			|| (isBasketball2K && currentPage === 'lbl_Sports')
		) && !isNumberGames(sport)
	);
	const { SiteConfigsData } = useGetSiteConfigs();
	const [view, oddsPageLayout] = useAppearanceStore(state => [state.view, state.oddsPageLayout]);
	const isOpened = useObservableState(profilePanelService.onProfilePanel$, false);
	const isShowTutorialButton = selectedLayoutSegments.includes('(main)') && view === ViewEnum.mars && oddsPageLayout === 'list';
	const [needShowTooltip, setNeedShowTooltip] = useState(localStorage.getItem(webStorageKeys.HAS_SHOWN_MARS_UPDATE_TOOLTIP_IN_HEADER) !== 'true');
	const [showParlayHelpModal, setShowParlayHelpModal] = useState(false);
	const taggedElement = useAnalyseAttribute('switch-to-odds-page-via-header');
	const isAthena = siteService.getLicenseeType() === LicenseeTypeEnums.Athena;
	const balancePanelRef = useRef<HTMLDivElement>(null);
	useOuterClick(balancePanelRef, () => setShowBalanceInfo(false));
	const { breakpoint } = useBreakpoint();
	const isMobile = breakpoint <= Breakpoint.sm;
	const [showHighlight, setShowHighlight] = useState(false);
	const isLoading = useAtomValue(isOddsPageLoadingAtom);

	const { user } = useAccountInfo();
	const userSetting = user?.UserSetting;
	const queryClient = useQueryClient();
	const params = useParams();
	const sportId = params.sport;

	const hasShownHighlight = accountService.getHasShownFeatureId(
		userSetting,
		PopUpFeatureId.BettypeModeNotice
	);

	const shouldShowHighlight = user?.ActStatus === AccountActStatus.Deposited && view === ViewEnum.mars && SiteConfigsData?.Enable1x2Customization && SiteConfigsData?.Enable1x2CustomizationHint && is1x2CustomizationSettingSport(Number(sportId)) && !isMobile && !hasShownHighlight && !isLoading;

	useEffect(() => {
		if (shouldShowHighlight) {
			// 等待頁面載入完成後，再等待 0.5 秒顯示 highlight
			const showTimer = setTimeout(() => {
				setShowHighlight(true);
			}, 500);

			// 顯示 3 秒後自動消失
			const hideTimer = setTimeout(() => {
				setShowHighlight(false);
				accountService.setHasShownFeatureId(queryClient, userSetting, PopUpFeatureId.BettypeModeNotice);
			}, 3500);

			return () => {
				clearTimeout(showTimer);
				clearTimeout(hideTimer);
			};
		}
	}, [isMobile, view, isLoading, userSetting]);

	/** show / hide balance info when width < 750 */
	const toggleBalanceInfo = () => {
		setKafka('bal-info');

		const willShowBalanceInfo = !showBalanceInfo;

		if (willShowBalanceInfo) {
			refetchBalanceInfo();
		}

		setShowBalanceInfo(willShowBalanceInfo);
	}

	/** show / hide balance when width >= 750 */
	const toggleBalance = () => {
		setKafka('bal');
		setIsShowBalance(!isShowBalance);
	}

	const toggleMePage = () => {
		setKafka('me');
		profilePanelService.displayProfilePanel(!isOpened);
	}

	const toggleParlayMode = () => {
		setKafka('parlay');

		wcPromotionMissionService.notifyMission({
			taskType: TaskTypeEnum.ClickSabaFeatures,
			productCode: 'parlay'
		});

		const sportMenu = menuTypeList.find(menu => menu.modeId === 0);
		if (!isParlayMode) {
			switchToParlay();
			setCurrentPage('lbl_mixparlay');
		} else {
			disableParlay();
			setCurrentPage('lbl_Sports');
		}
		if (sportMenu) {
			router.push(`/sports/${targetSport}/${targetMarket}`, { locale });
		}
	}

	const clickSearchButton = () => {
		setKafka('search');
		searchService.displaySearchModal(true)
	}

	const setKafka = (from: 'bal-info' | 'bal' | 'parlay' | 'parlay-help' | 'search' | 'me') => {
		if (view !== ViewEnum.mars) return; // 目前僅支援 mars

		let action;
		switch (from) {
			case 'bal-info':
				action = 3;
				break;
			case 'bal':
				action = isShowBalance ? 15 : 14; // 15: switch off, 14: switch on
				break;
			case 'parlay':
				action = isParlayMode ? 7 : 6; // 7: switch off, 6: switch on
				break;
			case 'parlay-help':
				action = 19;
				break;
			case 'search':
				action = window.innerWidth < 750 ? 2 : 13; // 2: mobile header, 13: desktop header
				break;
			case 'me':
				action = 16;
				break;
			default:
				break;
		}
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 2,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const openParlayHelp = () => {
		setKafka('parlay-help');
		setShowParlayHelpModal(true);
	}

	return (
		<div className={headerStyle.tools}>
			{
				isParlayMode && view === ViewEnum.mars? (
					<>
						<Button
							variant="icon"
							className={headerStyle.parlayHelpButton}
							onClick={openParlayHelp}
						>
							<QuestionIcon />
						</Button>
						<TutorialParlay
							isOpen={showParlayHelpModal}
							closeModal={() => setShowParlayHelpModal(false)}
						/>
					</>
				) : null
			}
			{
				showParlayButton ? (
					<Button
						shape="capsule"
						className={clsx({
							[headerStyle.parlayButton]: view === ViewEnum.mars,
							[headerStyle.parlayButtonActive]: isParlayMode && view === ViewEnum.mars,
						})}
						onClick={toggleParlayMode}
						ref={taggedElement}
					>
						{t('lbl_mixparlay')}
					</Button>
				) : null
			}
			{
				!SiteConfigsData?.EnableFilterEvent ?
					<SearchButton clickHandler={clickSearchButton} />
					: null
			}
			<Modal
				variant="full"
				isOpen={showSearchModal}
				closeModal={() => searchService.displaySearchModal(false)}
				showHeader={false}
			>
				<SearchComponent />
			</Modal>
			{isShowTutorialButton ? (
				<Button
					className={headerStyle.newbieWithTextButton}
					variant="icon"
					onClick={() => modalService.openNewbieTutorialModal()}
				>
					<QuestionIcon />
					<div>{t('lbl_NBetGuide')}</div>
					<TutorialTooltip className={tooltip({ visual: 'basic', horizontal: 'right' })} />
				</Button>
			) : null}

			{authToken ? (
				<>
					<div className={headerStyle.divider}></div>

					{/* width >= 750 點擊顯示/隱藏餘額 */}
					{actStatus === 2 ? (
						<div className={clsx('header__balance-wrap', headerStyle.balanceButton)}>
							<ProfileSimpleInfo
								toggleBalance={toggleBalance}
								showBalance={isShowBalance}
							/>
						</div>
					) : null}
					{/* width >= 750 點擊展開 me 彈窗 */}
					<Button
						variant="classic"
						className={headerStyle.meButton}
						onClick={toggleMePage}
					>
						<div className={headerStyle.avatar}>
							<UserIcon />
						</div>
						{SiteConfigsData?.EnableSpecialEvent ? <div className="festival-avatar-frame"></div> : null}
						{
							view === ViewEnum.mars && SiteConfigsData?.EnableMarsRevisionNotice && needShowTooltip ?
								(
									<Tooltip
										message={t('lbl_MarsNoticeInfo26')}
										showMessageDoAction={() => localStorage.setItem(webStorageKeys.HAS_SHOWN_MARS_UPDATE_TOOLTIP_IN_HEADER, 'true')}
										countdownTime={5000}
										clearMessage={() => setNeedShowTooltip(false)}
										className={tooltip({ visual: 'basic', horizontal: 'right', size: 'sm' })}
									/>
								) : null
						}

						<AccountTooltip position="avatar" />
					</Button>
					{/* width < 750 點擊展開 balance info */}
					{actStatus === 2 ? (
						<div className={headerStyle.userWrap}>
							<ProfileSimpleInfo
								showBalance={isShowBalance}
							/>
							{
								(user?.CanSeeAvailableFunds || user?.CanSeePendingFunds) &&
								<Button variant="classic" onClick={toggleBalanceInfo} className={clsx(headerStyle.userButton, { [headerStyle.userButtonActive]: showBalanceInfo })}>
									<DropdownIcon />
								</Button>
							}
						</div>
					) : null}
					{/* width < 750 點擊顯示/隱藏餘額 */}
					{showBalanceInfo ?
						isAthena ?
							(
								<div
									className={style.balancePanel}
									data-active={showBalanceInfo}
									ref={balancePanelRef}
								>
									<BalancePanel
										setShowBalancePanel={setShowBalanceInfo}
									/>
								</div>
							) :
							(
								<BalanceInfo
									setShowBalanceInfo={setShowBalanceInfo}
								/>
							) : null}
					{shouldShowHighlight && showHighlight && (
						<div className={headerStyle.circle}>
							<div className={headerStyle.innerCircle}>
								<div className={headerStyle.coreCircle} />
							</div>
						</div>)}
				</>
			) : null}
		</div>
	);
}
